import { useCallback, useEffect, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import ptBrLocale from "@fullcalendar/core/locales/pt-br";
import { api, formatApiError } from "../lib/api";
import { ROTINA } from "../constants/testIds";
import { X, Trash2, Loader2, Repeat } from "lucide-react";

// Categorias com as mesmas cores do backend (ROUTINE_CATEGORY_COLORS)
export const CATEGORIES = [
  { key: "escola", label: "Escola", color: "#3b82f6" },
  { key: "jiujitsu", label: "Jiu-jitsu", color: "#10b981" },
  { key: "estudo", label: "Estudo", color: "#8b5cf6" },
  { key: "prova", label: "Prova", color: "#ef4444" },
  { key: "outro", label: "Outro", color: "#f59e0b" },
];

const RECURRENCES = [
  { key: "none", label: "Não repete" },
  { key: "daily", label: "Todo dia" },
  { key: "weekdays", label: "Seg a sex" },
  { key: "weekly", label: "Toda semana neste dia" },
];

// datetime-local sem segundos, no fuso local do aluno
function toLocalInput(d) {
  const dt = new Date(d);
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

function toApiEvent(e) {
  return {
    id: e.id,
    title: e.title,
    start: e.start,
    end: e.end,
    backgroundColor: (e.color || "#8b5cf6") + "33",
    borderColor: e.color || "#8b5cf6",
    textColor: e.color || "#8b5cf6",
    extendedProps: { category: e.category, series_id: e.series_id, recurrence: e.recurrence, color: e.color },
  };
}

export default function Rotina() {
  const calRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [modal, setModal] = useState(null); // {mode:"new", start, end} | {mode:"edit", event}
  const [scopeAsk, setScopeAsk] = useState(null); // {message, onOne, onSeries, onCancel}

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/routine");
      setEvents(data.map(toApiEvent));
      setErr(null);
    } catch (e) {
      setErr(formatApiError(e));
    }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Pergunta "só este / toda a série" quando o evento pertence a uma série
  const askScope = (message) =>
    new Promise((resolve) => {
      setScopeAsk({
        message,
        onOne: () => { setScopeAsk(null); resolve("one"); },
        onSeries: () => { setScopeAsk(null); resolve("series"); },
        onCancel: () => { setScopeAsk(null); resolve(null); },
      });
    });

  const moveOrResize = async (info) => {
    const { event } = info;
    const isSeries = !!event.extendedProps.series_id;
    let scope = "one";
    if (isSeries) {
      scope = await askScope(`Mudar o horário de "${event.title}"?`);
      if (!scope) { info.revert(); return; }
    }
    try {
      await api.put(`/routine/${event.id}`, {
        start: event.start.toISOString(),
        end: (event.end || new Date(event.start.getTime() + 3600000)).toISOString(),
        scope,
      });
      refresh();
    } catch (e) {
      setErr(formatApiError(e));
      info.revert();
    }
  };

  return (
    <div data-testid={ROTINA.page} className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Rotina</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Arraste pra mover, puxe a borda pra esticar, clique num espaço vazio pra criar.
          </p>
        </div>
      </div>

      {/* Legenda de categorias */}
      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map((c) => (
          <span key={c.key} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-[11px] text-muted-foreground">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: c.color }} /> {c.label}
          </span>
        ))}
      </div>

      {err && <p className="text-sm text-red-400">{err}</p>}

      <div className="fc-theme-app rounded-2xl border border-border bg-card p-2 sm:p-3">
        <FullCalendar
          ref={calRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          locale={ptBrLocale}
          initialView="timeGridWeek"
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "dayGridMonth,timeGridWeek,timeGridDay",
          }}
          height="auto"
          nowIndicator
          allDaySlot={false}
          slotMinTime="05:00:00"
          slotMaxTime="24:00:00"
          scrollTime="07:00:00"
          firstDay={1}
          editable
          eventDurationEditable
          selectable
          selectMirror
          longPressDelay={300}
          dayMaxEventRows={4}
          events={events}
          select={(info) => {
            setModal({ mode: "new", start: info.start, end: info.end });
            info.view.calendar.unselect();
          }}
          dateClick={(info) => {
            // No mês, clicar num dia cria evento de 1h às 14h daquele dia
            if (info.view.type === "dayGridMonth") {
              const s = new Date(info.date); s.setHours(14, 0, 0, 0);
              setModal({ mode: "new", start: s, end: new Date(s.getTime() + 3600000) });
            }
          }}
          eventClick={(info) => setModal({ mode: "edit", event: info.event })}
          eventDrop={moveOrResize}
          eventResize={moveOrResize}
        />
      </div>

      {loading && events.length === 0 && (
        <p className="text-center text-sm text-muted-foreground py-2 flex items-center justify-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-violet-400" /> Carregando sua semana...
        </p>
      )}
      {!loading && events.length === 0 && !err && (
        <p className="text-center text-sm text-muted-foreground py-2">
          Sua semana está vazia. Clique e arraste numa faixa do calendário pra criar o primeiro evento 🎯
        </p>
      )}

      {modal && (
        <EventModal
          modal={modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); refresh(); }}
          askScope={askScope}
        />
      )}

      {scopeAsk && <ScopeDialog {...scopeAsk} />}
    </div>
  );
}

function ScopeDialog({ message, onOne, onSeries, onCancel }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Repeat className="h-4 w-4 text-violet-400" />
          <h3 className="font-bold tracking-tight text-sm">{message}</h3>
        </div>
        <p className="text-xs text-muted-foreground">Esse evento se repete. Aplicar em:</p>
        <div className="flex flex-col gap-2">
          <button onClick={onOne} className="rounded-full bg-violet-600 hover:bg-violet-500 text-white py-2.5 text-sm font-medium transition">
            Só este dia
          </button>
          <button onClick={onSeries} className="rounded-full border border-border hover:border-violet-500/50 py-2.5 text-sm font-medium transition">
            Toda a série
          </button>
          <button onClick={onCancel} className="py-1.5 text-xs text-muted-foreground hover:text-foreground">
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

function EventModal({ modal, onClose, onSaved, askScope }) {
  const editing = modal.mode === "edit" ? modal.event : null;
  const [title, setTitle] = useState(editing?.title || "");
  const [category, setCategory] = useState(editing?.extendedProps?.category || "estudo");
  const [start, setStart] = useState(toLocalInput(editing?.start || modal.start || new Date()));
  const [end, setEnd] = useState(toLocalInput(editing?.end || modal.end || new Date(Date.now() + 3600000)));
  const [recurrence, setRecurrence] = useState("none");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const isSeries = !!editing?.extendedProps?.series_id;
  const color = CATEGORIES.find((c) => c.key === category)?.color || "#8b5cf6";

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      if (editing) {
        let scope = "one";
        if (isSeries) {
          scope = await askScope(`Salvar mudanças em "${editing.title}"?`);
          if (!scope) { setBusy(false); return; }
        }
        await api.put(`/routine/${editing.id}`, {
          title,
          category,
          color,
          start: new Date(start).toISOString(),
          end: new Date(end).toISOString(),
          scope,
        });
      } else {
        await api.post("/routine", {
          title: title || "Evento",
          category,
          color,
          start: new Date(start).toISOString(),
          end: new Date(end).toISOString(),
          recurrence,
        });
      }
      onSaved();
    } catch (e) {
      setErr(formatApiError(e));
      setBusy(false);
    }
  };

  const del = async () => {
    let scope = "one";
    if (isSeries) {
      scope = await askScope(`Excluir "${editing.title}"?`);
      if (!scope) return;
    } else if (!window.confirm("Apagar esse evento?")) {
      return;
    }
    setBusy(true);
    try {
      await api.delete(`/routine/${editing.id}?scope=${scope}`);
      onSaved();
    } catch (e) {
      setErr(formatApiError(e));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="w-full sm:max-w-md bg-card border border-border rounded-t-3xl sm:rounded-2xl">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h2 className="font-bold tracking-tight">{editing ? "Editar evento" : "Novo evento"}</h2>
          <button onClick={onClose} className="rounded-full p-1.5 hover:bg-muted"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Título">
            <input
              data-testid={ROTINA.eventTitleInput}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex.: Revisar química"
              autoFocus
              className="w-full rounded-lg bg-secondary px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500/50"
            />
          </Field>
          <Field label="Categoria / cor">
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setCategory(c.key)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition ${
                    category === c.key ? "border-transparent text-white" : "border-border text-muted-foreground hover:text-foreground"
                  }`}
                  style={category === c.key ? { backgroundColor: c.color } : undefined}
                >
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: category === c.key ? "#fff" : c.color }} />
                  {c.label}
                </button>
              ))}
            </div>
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Início">
              <input data-testid={ROTINA.eventStart} type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} className="w-full rounded-lg bg-secondary px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500/50" />
            </Field>
            <Field label="Fim">
              <input data-testid={ROTINA.eventEnd} type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} className="w-full rounded-lg bg-secondary px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500/50" />
            </Field>
          </div>
          {!editing && (
            <Field label="Repetir">
              <select value={recurrence} onChange={(e) => setRecurrence(e.target.value)} className="w-full rounded-lg bg-secondary px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500/50">
                {RECURRENCES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
            </Field>
          )}
          {isSeries && (
            <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <Repeat className="h-3 w-3" /> Esse evento faz parte de uma série — ao salvar/excluir você escolhe se vale só pra este dia.
            </p>
          )}
          {err && <p className="text-sm text-red-400">{err}</p>}
        </div>
        <div className="p-4 border-t border-border flex gap-2 items-center">
          {editing && (
            <button data-testid={ROTINA.eventDelete} onClick={del} disabled={busy} className="rounded-full px-3 py-2.5 text-sm text-red-400 hover:bg-red-500/10 inline-flex items-center gap-1.5">
              <Trash2 className="h-3.5 w-3.5" /> Excluir
            </button>
          )}
          <div className="flex-1" />
          <button
            data-testid={ROTINA.eventSave}
            onClick={save}
            disabled={busy || !title.trim()}
            className="rounded-full bg-violet-600 hover:bg-violet-500 text-white px-5 py-2.5 text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2"
          >
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">{label}</span>
      {children}
    </label>
  );
}
